import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, Sparkles, ClipboardList, Plus, X, Check, Trash2, Award } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const STATUS_STYLE = {
  clean:        { label: "Clean",        color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dirty:        { label: "Dirty",        color: "bg-rose-50 text-rose-700 border-rose-200" },
  inspected:    { label: "Inspected",    color: "bg-blue-50 text-blue-700 border-blue-200" },
  out_of_order: { label: "Out of Order", color: "bg-gray-100 text-gray-500 border-gray-300" },
};
const STATUS_OPTIONS = Object.keys(STATUS_STYLE);
const TASK_COLUMNS = [
  { key: "pending",     label: "Pending" },
  { key: "in-progress", label: "In Progress" },
  { key: "done",        label: "Done" },
];

function NewTaskModal({ units, staff, onClose, onSaved, authHeaders }) {
  const [form, setForm] = useState({ unit: "", type: "cleaning", assignedTo: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!form.unit) { setError("Pick a room/unit."); return; }
    const [roomId, unitId] = form.unit.split("|");
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/housekeeping/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ roomId, unitId, type: form.type, assignedTo: form.assignedTo || undefined, notes: form.notes }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(json.data); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">New Task</h2>
          <button onClick={onClose} className="p-1.5 -m-1.5"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Room *</label>
            <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
              <option value="">Select a room</option>
              {units.map(u => (
                <option key={u.unitId} value={`${u.roomId}|${u.unitId}`}>{u.roomName} · {u.roomNumber || "—"}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                <option value="cleaning">Cleaning</option>
                <option value="inspection">Inspection</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Assign To</label>
              <select value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                <option value="">Unassigned</option>
                {staff.map(s => <option key={s._id} value={s._id}>{s.fullName}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={submit} disabled={saving}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white
              font-semibold text-sm shadow-sm hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 transition-all">
            {saving ? "Creating…" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HousekeepingPage() {
  const { authHeaders } = useAuth();
  const [units, setUnits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/housekeeping/board`, { headers: authHeaders() });
      const json = await res.json();
      setUnits(json.data?.units || []);
      setTasks(json.data?.tasks || []);
      setStaff(json.data?.staff || []);
    } catch {}
    setLoading(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  async function setUnitStatus(u, status) {
    const res = await fetch(`${API}/rooms/${u.roomId}/units/${u.unitId}/housekeeping-status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ status }),
    });
    if (res.ok) load();
  }

  async function setTaskStatus(task, status) {
    const res = await fetch(`${API}/housekeeping/tasks/${task._id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ status }),
    });
    if (res.ok) load();
  }

  async function removeTask(id) {
    if (!window.confirm("Delete this task?")) return;
    const res = await fetch(`${API}/housekeeping/tasks/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) load();
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Housekeeping</h1>
          <p className="text-sm text-gray-500">{units.length} room{units.length !== 1 ? "s" : ""} · {tasks.length} open task{tasks.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setShowNew(true)}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-white
              bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-xl
              shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all">
            <Plus size={15} /> New Task
          </button>
        </div>
      </div>

      {/* Room status grid */}
      <div>
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-3">
          <Sparkles size={15} className="text-amber-500" /> Room Status
        </h2>
        {loading ? (
          <div className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>
        ) : units.length === 0 ? (
          <p className="text-center py-12 text-gray-400 bg-white rounded-2xl border border-gray-100">No rooms yet</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {units.map(u => {
              const s = STATUS_STYLE[u.housekeepingStatus] || STATUS_STYLE.clean;
              return (
                <div key={u.unitId} className={`rounded-2xl border p-4 ${s.color}`}>
                  <p className="font-semibold text-gray-900 truncate">{u.roomName}</p>
                  <p className="text-xs text-gray-500 mb-2">Room {u.roomNumber || "—"}</p>
                  <select value={u.housekeepingStatus} onChange={e => setUnitStatus(u, e.target.value)}
                    className="w-full text-xs font-medium rounded-lg px-2 py-2.5 border bg-white/70 focus:outline-none">
                    {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{STATUS_STYLE[opt].label}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Task board */}
      <div>
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-3">
          <ClipboardList size={15} className="text-amber-500" /> Today's Tasks
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {TASK_COLUMNS.map(col => (
            <div key={col.key} className="bg-white rounded-2xl border border-gray-100 p-3 space-y-2 min-h-[100px]">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">{col.label}</p>
              {tasks.filter(t => t.status === col.key).map(t => (
                <div key={t._id} className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 capitalize">{t.type}</p>
                    <button onClick={() => removeTask(t._id)} className="p-1 -m-1 text-gray-400 hover:text-red-500">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {t.assignedTo?.fullName && <p className="text-xs text-gray-500">👤 {t.assignedTo.fullName}</p>}
                  {t.notes && <p className="text-xs text-gray-500">{t.notes}</p>}
                  <div className="flex gap-1.5 pt-1">
                    {col.key !== "pending" && (
                      <button onClick={() => setTaskStatus(t, "pending")} className="text-[10.5px] px-2 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">Pending</button>
                    )}
                    {col.key !== "in-progress" && (
                      <button onClick={() => setTaskStatus(t, "in-progress")} className="text-[10.5px] px-2 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100">Start</button>
                    )}
                    {col.key !== "done" && (
                      <button onClick={() => setTaskStatus(t, "done")} className="text-[10.5px] px-2 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center gap-1">
                        <Check size={10} /> Done
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {tasks.filter(t => t.status === col.key).length === 0 && (
                <p className="text-xs text-gray-300 text-center py-4">Nothing here</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <StaffPerformanceSection authHeaders={authHeaders} />

      {showNew && <NewTaskModal units={units} staff={staff} onClose={() => setShowNew(false)} onSaved={t => setTasks(prev => [t, ...prev])} authHeaders={authHeaders} />}
    </div>
  );
}

// Last-30-days leaderboard — how many tasks each staff member completed
// and how fast on average (createdAt -> completedAt).
function StaffPerformanceSection({ authHeaders }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    fetch(`${API}/housekeeping/staff-performance?from=${from}`, { headers: authHeaders() })
      .then(r => r.json()).then(j => setRows(j.data || [])).catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  function fmtTurnaround(mins) {
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  }

  return (
    <div>
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-3">
        <Award size={15} className="text-amber-500" /> Staff Performance (last 30 days)
      </h2>
      <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
        {loading ? (
          <div className="text-center py-8 text-gray-400"><RefreshCw size={18} className="animate-spin mx-auto mb-2" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-sm">No completed tasks yet</p>
        ) : rows.map((r, i) => (
          <div key={r.staffId || i} className="flex items-center justify-between px-4 py-3">
            <p className="text-sm font-medium text-gray-900">{r.staffName}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>{r.tasksCompleted} task{r.tasksCompleted !== 1 ? "s" : ""}</span>
              <span>avg {fmtTurnaround(r.avgTurnaroundMinutes)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
